/**
 * useRadarUserPrefs — per-user radar view preferences (filter-fix-v2).
 *
 * Loads effective prefs (time range + filter state) from the server and
 * persists changes with debounce + latest-wins sequencing. localStorage is
 * used as a cache and for a one-time migration of the legacy time slider.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import type {
  RadarType,
  RadarFilterState,
  UserRadarFilterResponse,
} from '@/services/api/teacher'
import { mergeDefault, countActiveFilters } from '../utils/filterBubbles'
import { cloneFilterState, defaultFilterForRadarType } from '../types/filter'
import { getSavedTimeRangeDays, saveTimeRangeDays } from '@/utils/radarStorage'

const MIGRATION_FLAG_KEY = 'radar_prefs_migrated'
const DEBOUNCE_MS = 300

function migrationFlagKey(radarType: RadarType): string {
  return `${MIGRATION_FLAG_KEY}:${radarType}`
}

export interface RadarUserPrefsApi {
  getRadarUserFilters: (radarType: RadarType, signal?: AbortSignal) => Promise<UserRadarFilterResponse>
  saveRadarUserFilters: (payload: {
    radar_type: RadarType
    time_range_days?: number | null
    filter_state?: RadarFilterState
  }) => Promise<UserRadarFilterResponse>
}

interface UseRadarUserPrefsResult {
  filterState: RadarFilterState
  timeRangeDays: number | null
  defaultState: RadarFilterState
  allowOverride: boolean
  isLocked: boolean
  isLoaded: boolean
  activeFilterCount: number
  setFilter: (partial: Partial<RadarFilterState>) => void
  setTimeRange: (days: number) => void
  resetFilter: () => void
}

export function useRadarUserPrefs(
  radarType: RadarType,
  api: RadarUserPrefsApi,
  fallbackTimeRangeDays?: number,
): UseRadarUserPrefsResult {
  const [filterState, setFilterState] = useState<RadarFilterState>(() =>
    cloneFilterState(defaultFilterForRadarType(radarType)),
  )
  const [timeRangeDays, setTimeRangeDaysState] = useState<number | null>(null)
  const [defaultState, setDefaultState] = useState<RadarFilterState>(() =>
    cloneFilterState(defaultFilterForRadarType(radarType)),
  )
  const [allowOverride, setAllowOverride] = useState(true)
  const [isLocked, setIsLocked] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const loadGenRef = useRef(0)
  const saveGenRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPayloadRef = useRef<{ time_range_days?: number | null; filter_state?: RadarFilterState }>({})
  const radarTypeRef = useRef(radarType)
  const apiRef = useRef(api)
  const dirtyFilterRef = useRef(false)
  const dirtyTimeRangeRef = useRef(false)
  apiRef.current = api
  radarTypeRef.current = radarType

  // ---- Persist with debounce + latest-wins ----
  const mountedRef = useRef(true)

  const flush = useCallback(() => {
    const payload = { ...pendingPayloadRef.current }
    pendingPayloadRef.current = {}
    if (payload.time_range_days === undefined && payload.filter_state === undefined) return
    const gen = ++saveGenRef.current
    apiRef.current
      .saveRadarUserFilters({ radar_type: radarTypeRef.current, ...payload })
      .then((res: UserRadarFilterResponse) => {
        if (!mountedRef.current || gen !== saveGenRef.current) return
        setDefaultState(mergeDefault(res.default_state, radarTypeRef.current))
        setAllowOverride(res.allow_override)
        setIsLocked(res.is_locked)
      })
      .catch((err) => {
        // Aborted (superseded) or locked-teacher 403 are expected; log others.
        const e = err as { name?: string; statusCode?: number }
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
        if (e?.statusCode === 403) return
        console.warn('[useRadarUserPrefs] save failed:', err)
      })
  }, [])

  const scheduleSave = useCallback(
    (partial: { time_range_days?: number | null; filter_state?: RadarFilterState }) => {
      pendingPayloadRef.current = { ...pendingPayloadRef.current, ...partial }
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(flush, DEBOUNCE_MS)
    },
    [flush],
  )

  // ---- Load on mount / radarType change ----
  useEffect(() => {
    let cancelled = false
    abortRef.current?.abort()
    const gen = ++loadGenRef.current
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoaded(false)
    api
      .getRadarUserFilters(radarType, controller.signal)
      .then((res) => {
        if (cancelled || gen !== loadGenRef.current) return
        if (!dirtyFilterRef.current) {
          setFilterState(mergeDefault(res.filter_state, radarType))
        }
        setDefaultState(mergeDefault(res.default_state, radarType))
        setAllowOverride(res.allow_override)
        setIsLocked(res.is_locked)

        if (res.is_locked && res.time_range_days == null) {
          setTimeRangeDaysState(null)
          setIsLoaded(true)
          return
        }

        if (!dirtyTimeRangeRef.current) {
          // Time range: server value, else one-time localStorage migration per radar type.
          let days = res.time_range_days
          const flagKey = migrationFlagKey(radarType)
          if (days == null) {
            const migrated = localStorage.getItem(flagKey) === '1'
            const local = getSavedTimeRangeDays(radarType)
            if (!migrated && local != null) {
              days = local
              localStorage.setItem(flagKey, '1')
              scheduleSave({ time_range_days: local })
            } else if (local != null) {
              days = local
            } else if (fallbackTimeRangeDays != null) {
              days = fallbackTimeRangeDays
            }
          } else {
            saveTimeRangeDays(days, radarType)
          }
          setTimeRangeDaysState(days)
        }
        setIsLoaded(true)
      })
      .catch((err) => {
        if (cancelled || gen !== loadGenRef.current) return
        const e = err as { name?: string }
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
        console.warn('[useRadarUserPrefs] load failed:', err)
        setIsLoaded(true)
      })

    return () => {
      cancelled = true
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radarType])

  // ---- Cleanup ----
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      const payload = pendingPayloadRef.current
      if (payload.time_range_days !== undefined || payload.filter_state !== undefined) {
        pendingPayloadRef.current = {}
        apiRef.current
          .saveRadarUserFilters({ radar_type: radarTypeRef.current, ...payload })
          .catch(() => {})
      }
      abortRef.current?.abort()
    }
  }, [])

  // ---- Public actions (no-op when locked) ----
  // filterState is mirrored in a ref so setFilter can compute the merged next
  // state synchronously and persist the exact value (setState is async).
  const filterStateRef = useRef(filterState)
  filterStateRef.current = filterState

  const setFilter = useCallback(
    (partial: Partial<RadarFilterState>) => {
      if (isLocked) return
      const next = cloneFilterState(filterStateRef.current)
      for (const k of ['status', 'zones', 'types', 'subjects', 'faculties', 'classes', 'requesters'] as const) {
        if (Array.isArray(partial[k])) next[k] = [...partial[k]] as never
      }
      if (partial.specials) next.specials = { ...next.specials, ...partial.specials }
      if (typeof partial.activeOnly === 'boolean') next.activeOnly = partial.activeOnly
      filterStateRef.current = next
      dirtyFilterRef.current = true
      setFilterState(next)
      scheduleSave({ filter_state: next })
    },
    [isLocked, scheduleSave],
  )

  const setTimeRange = useCallback(
    (days: number) => {
      if (isLocked) return
      dirtyTimeRangeRef.current = true
      setTimeRangeDaysState(days)
      saveTimeRangeDays(days, radarTypeRef.current)
      scheduleSave({ time_range_days: days })
    },
    [isLocked, scheduleSave],
  )

  const resetFilter = useCallback(() => {
    if (isLocked) return
    const reset = cloneFilterState(defaultState)
    filterStateRef.current = reset
    dirtyFilterRef.current = true
    setFilterState(reset)
    scheduleSave({ filter_state: reset })
  }, [isLocked, defaultState, scheduleSave])

  return {
    filterState,
    timeRangeDays,
    defaultState,
    allowOverride,
    isLocked,
    isLoaded,
    activeFilterCount: countActiveFilters(filterState, defaultState),
    setFilter,
    setTimeRange,
    resetFilter,
  }
}
